<script setup>
import moment from 'moment'
import { SurveyType } from '@/enums/survey/survey.type'
import { useApi } from '@/composables/useApi'
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import HorizontalStackBar from '@/@core/components/graph/HorizontalStackBar.vue'
import HorizontalBar from '@/@core/components/graph/HorizontalBar.vue'
import VerticalBar from '@/@core/components/graph/VerticalBar.vue'
import { VSkeletonLoader } from 'vuetify/lib/labs/components.mjs'
import { cookieRef } from '@/@layouts/stores/config'
import SubscriptionTable from '@/views/survey/SubscriptionTable.vue'

definePage({
  meta: {
    name: "Survey Analytics",
  },
})

const route = useRoute()
const router = useRouter()

const unitId = computed(() => cookieRef('selectedUnit').value ?? null)

const surveyType = computed(() => route.params.type)
const surveyId = computed(() => route.params.survey ?? null)

const survey = ref()

// Validate Survey Type
if (!surveyType) {
  router.push({
    name: 'invalid-url',
    params: { code: 404 },
  })
}

if (surveyId.value) {
  await useApi(createUrl(`/survey/surveys/${surveyId.value}`), {
    beforeFetch: () => {},
    afterFetch: ({ data }) => {
      survey.value = data
    },
    onFetchError: error => {
      router.push({
        name: 'invalid-url',
        params: { code: 404 },
      })
    },
  })
}

// Default date range - Last 30 days
const defaultFromDate = moment().subtract(30, 'days').format('YYYY-MM-DD')
const defaultToDate = moment().format('YYYY-MM-DD')

// Filter State
const selectedSchedule = ref(null)
const scheduleDateRange = ref(`${defaultFromDate} to ${defaultToDate}`)
const fromDate = ref(defaultFromDate)
const toDate = ref(defaultToDate)

// Schedules computed from survey
const schedules = computed(() => survey.value?.schedules ?? [])

// Date Range Options
const dateRangeOptions = ref({
  mode: 'range',
  maxDate: new Date(),
  dateFormat: 'Y-m-d',
  enableTime: false,
  defaultDate: [defaultFromDate, defaultToDate],
  onChange: (selectedDates, dateStr) => {
    if (selectedDates.length > 0) {
      fromDate.value = moment(selectedDates[0]).format('YYYY-MM-DD')
      toDate.value = selectedDates[1]
        ? moment(selectedDates[1]).format('YYYY-MM-DD')
        : fromDate.value
    }
  },
})

// Specialties filter (CUSTOMER type only)
const selectedSpecialty = ref(null)

const { data: specialties } = useApi(createUrl('/masters/episode/specialties'), {
  beforeFetch: ({ cancel }) => {
    if (surveyType.value !== SurveyType.CUSTOMER) cancel()
  },
})

// Whether a schedule has been selected
const isScheduleSelected = computed(() => !!selectedSchedule.value)

// Computed filter query params
const filterQuery = computed(() => {
  const query = {
    unit_id: unitId.value,
    survey_schedule_id: selectedSchedule.value,
    schedule_at: [fromDate.value, toDate.value],
  }

  if (surveyType.value === SurveyType.CUSTOMER && selectedSpecialty.value) {
    query['episode_specialty'] = selectedSpecialty.value
  }

  return query
})

// Analytics data
const analyticsData = ref(null)

const fetchAnalytics = () => {
  if (!isScheduleSelected.value) return

  analyticsData.value = null

  useApi(createUrl(`/survey/surveys/${surveyId.value}/analytics`, {
    query: filterQuery,
  }), {
    afterFetch: ({ data }) => {
      analyticsData.value = data
      survey.value = {
        ...survey.value,
        ...data,
      }
    },
  })
}

// Load Form Steps
const isLoadingFormSteps = ref(false)
const formSteps = ref(null)

const fetchFormSteps = () => {
  if (!isScheduleSelected.value) return

  isLoadingFormSteps.value = true

  useApi(createUrl(`/survey/form-steps`, {
    query: {
      search: {
        survey_id: surveyId.value,
      },
      with: ['questionGroups'],
    },
  }), {
    afterFetch: ({ data }) => {
      formSteps.value = data
      isLoadingFormSteps.value = false
    },
    onFetchError: () => {
      isLoadingFormSteps.value = false
    },
  })
}

const questions = ref([])
const isCalculatingQuestionAnalytics = ref(false)

// Generation counter to discard stale API responses
let analyticsGeneration = 0

const calculateQuestionAnalytics = () => {
  if (!formSteps.value) return

  const currentGeneration = ++analyticsGeneration

  questions.value = []
  isCalculatingQuestionAnalytics.value = true

  let pendingRequests = 0

  formSteps.value.forEach(step => {
    step.question_groups.forEach(group => {
      const filterQuestions = group.questions.filter(item => ['select', 'radio', 'radio_rating', 'star_rating', 'smile', 'scale'].includes(item.question_type))

      filterQuestions.forEach(question => {
        pendingRequests++

        // Load Question Response
        useApi(createUrl(`/survey/questions/${question.id}`, {
          query: filterQuery,
        }), {
          afterFetch: ({ data }) => {
            // Discard if a newer calculation has started
            if (currentGeneration !== analyticsGeneration) return

            const grouped = data.answers.reduce((acc, item) => {
              if (!item.question_option_id) return acc

              const option = question.question_options.find(x => x.id === item.question_option_id)

              if (!option) return acc

              if (!acc[option.id]) {
                acc[option.id] = {
                  question_option_id: option.id,
                  label: option.label,
                  value: option.value,
                  count: 0,
                  responses: [],
                  color: option.color,
                }
              }

              acc[option.id].count += 1
              acc[option.id].responses.push(item)

              return acc
            }, {})

            const totalAnswers = data.answers.length

            let graphData = Object.values(grouped).map(group => ({
              question_option_id: group.question_option_id,
              label: group.label,
              value: group.value,
              count: group.count,
              percentage: ((group.count / totalAnswers) * 100).toFixed(2),
              color: group.color,
            }))

            // For Scale Type Questions, include all options even if count is 0
            if (question.question_type === 'scale') {
              const scaleColors = colorPaletteForNPS()

              graphData = question.question_options.map((item, optionIndex) => {
                let data = graphData.find(x => x.question_option_id === item.id)

                return {
                  question_option_id: item.id,
                  label: item.label,
                  value: item.value,
                  count: data?.count ?? 0,
                  percentage: data?.percentage ?? 0,
                  color: item.color ?? scaleColors[optionIndex],
                }
              })
            }

            if (totalAnswers > 0) {
              questions.value.push({
                ...data,
                total_answers: totalAnswers,
                graph_data: graphData,
              })
            }

            pendingRequests--
            if (pendingRequests === 0) {
              isCalculatingQuestionAnalytics.value = false
            }
          },
          onFetchError: () => {
            if (currentGeneration !== analyticsGeneration) return

            pendingRequests--
            if (pendingRequests === 0) {
              isCalculatingQuestionAnalytics.value = false
            }
          },
        })
      })
    })
  })

  // If no requests were made, stop loading
  if (pendingRequests === 0) {
    isCalculatingQuestionAnalytics.value = false
  }
}

watch(() => formSteps.value, value => {
  if (value) {
    calculateQuestionAnalytics()
  }
}, {
  deep: true,
})

// Watch schedule selection — load data when selected, clear when deselected
watch(() => selectedSchedule.value, value => {
  questions.value = []
  analyticsData.value = null

  if (value) {
    fetchAnalytics()

    // Only fetch form steps once, then just recalculate
    if (formSteps.value) {
      calculateQuestionAnalytics()
    } else {
      fetchFormSteps()
    }
  }
})

// Watch date range or specialty changes — reload only if schedule is selected
watch(() => [fromDate.value, toDate.value, selectedSpecialty.value], () => {
  if (!isScheduleSelected.value) return

  fetchAnalytics()
  calculateQuestionAnalytics()
})

const isLoading = computed(() => isLoadingFormSteps.value || isCalculatingQuestionAnalytics.value)

// Export
const toast = useToast()
const isExporting = ref(false)

// Statuses that src/utils/api.js's `onResponse` handler already shows a toast
// for (403, 404, 422, 500), plus 401 which it handles by silently redirecting
// to login instead of toasting. Keep this in sync with that handler — if its
// status list changes, this list must change too.
const STATUSES_ALREADY_HANDLED_BY_API_CLIENT = [401, 403, 404, 422, 500]

const exportAnalytics = async () => {
  isExporting.value = true

  try {
    // Progress, download and failure toasts are handled globally by ExportSnackbar,
    // which listens on the export-status channel.
    await $api(createUrl(`/survey/surveys/${surveyId.value}/analytics/export`, {
      query: filterQuery,
    }).value)
  } catch (error) {
    // src/utils/api.js's `onResponseError` attaches a numeric `error.status`
    // for every HTTP error response (not just the ones it toasts for), and
    // its `onRequestError` already toasts unconditionally on transport-level
    // failures (network down, aborted) — those errors have no numeric status.
    // So: toast here only when we have a numeric status that api.js did NOT
    // already surface a toast for (e.g. 503, 429, 400, 409, 502). Otherwise
    // either api.js already toasted, or api.js redirected silently on 401.
    if (typeof error?.status === 'number' && !STATUSES_ALREADY_HANDLED_BY_API_CLIENT.includes(error.status)) {
      toast.error('Failed to start export', 'error')
    }
  } finally {
    isExporting.value = false
  }
}

// Subscription Table Dialog
const isSubscriptionDialogVisible = ref(false)
const subscriptionTableFilter = ref(null)

const handleDataPointClick = data => {
  subscriptionTableFilter.value = {
    ...filterQuery.value,
    question_option_id: data.question_option_id,
  }
  isSubscriptionDialogVisible.value = true
}

watch(
  () => isSubscriptionDialogVisible.value,
  value => {
    if (value === false) subscriptionTableFilter.value = null
  },
)
</script>

<template>
  <VRow>
    <VCol cols="12">
      <div class="d-flex align-center flex-wrap gap-4">
        <div class="d-flex align-center gap-2">
          <h4 class="text-h3 font-weight-bold text-primary text-capitalize">
            {{ survey.title }}
          </h4>
          <!--
            <VBtn
            icon
            size="small"
            density="compact"
            variant="text"
            color="primary"
            :to="{ name: 'app-survey-customer-setup', params: { type: surveyType, survey: surveyId } }"
            >
            <VIcon
            icon="tabler-settings"
            size="20"
            />
            <VTooltip
            activator="parent"
            location="top"
            >
            Settings
            </VTooltip>
            </VBtn>
            <VBtn
            icon
            size="small"
            variant="text"
            color="primary"
            :to="{ name: 'app-survey-collection-customer', params: { id: surveyId } }"
            >
            <VIcon
            icon="tabler-user-screen"
            size="20"
            />
            <VTooltip
            activator="parent"
            location="top"
            >
            View Responses
            </VTooltip>
            </VBtn> 
          -->
        </div>
        <VSpacer />
        <div class="d-flex align-center flex-wrap gap-4">
          <AppSelect
            v-model="selectedSchedule"
            placeholder="Select Schedule"
            :items="schedules"
            :item-title="item => item.name"
            :item-value="item => item.id"
            clearable
            style="min-inline-size: 200px;"
          />
          <AppSelect
            v-if="surveyType === SurveyType.CUSTOMER"
            v-model="selectedSpecialty"
            placeholder="Select Specialty"
            :items="specialties ?? []"
            :item-title="item => item.specialty"
            :item-value="item => item.specialty"
            clearable
            style="min-inline-size: 200px; max-inline-size: 300px;"
          />
          <AppDateTimePicker
            v-model="scheduleDateRange"
            placeholder="Select Date Range"
            :config="dateRangeOptions"
            style="min-inline-size: 250px;"
          />
          <VBtn
            variant="flat"
            prepend-icon="tabler-download"
            :loading="isExporting"
            :disabled="!isScheduleSelected || isLoading"
            @click="exportAnalytics"
          >
            Export
          </VBtn>
        </div>
      </div>
    </VCol>

    <!-- No Schedule Selected Message -->
    <VCol
      v-if="!isScheduleSelected"
      cols="12"
    >
      <VCard
        variant="flat"
        class="border"
      >
        <VCardText class="d-flex flex-column align-center justify-center py-12">
          <VIcon
            icon="tabler-calendar-search"
            size="64"
            color="primary"
            class="mb-4"
          />
          <h6 class="text-h6 mb-2">
            Select a Schedule to View Analytics
          </h6>
          <span class="text-body-2 text-medium-emphasis">
            Please select a schedule from the dropdown above to load analytics data.
          </span>
        </VCardText>
      </VCard>
    </VCol>

    <!-- Analytics Content (only when schedule is selected) -->
    <template v-else>
      <!-- Loading Skeleton -->
      <template v-if="isLoading">
        <!-- Summary Cards Skeleton -->
        <VCol
          v-for="n in 3"
          :key="n"
          cols="12"
          sm="4"
        >
          <VSkeletonLoader type="list-item-two-line, avatar" />
        </VCol>

        <!-- Question Cards Skeleton -->
        <VCol
          v-for="n in 3"
          :key="`q-${n}`"
          cols="12"
        >
          <VSkeletonLoader type="heading, divider, image" />
        </VCol>
      </template>

      <template v-else-if="questions?.length > 0">
        <!-- Total Scheduled Surveys Card -->
        <VCol
          cols="12"
          sm="4"
        >
          <VCard
            variant="flat"
            class="border pa-4"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h4 font-weight-bold primary--text">
                  {{ survey.number_of_scheduled_surveys }}
                </div>
                <div class="text-subtitle-1 text-medium-emphasis mt-1">
                  Scheduled Surveys
                </div>
              </div>
              <VAvatar
                color="primary"
                size="56"
                class="elevation-1"
              >
                <VIcon
                  icon="tabler-clipboard-text"
                  size="32"
                />
              </VAvatar>
            </div>
          </VCard>
        </VCol>

        <!-- Collected Surveys Card -->
        <VCol
          cols="12"
          sm="4"
        >
          <VCard
            variant="flat"
            class="border pa-4"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h4 font-weight-bold success--text">
                  {{ survey.number_of_collected_surveys }}
                </div>
                <div class="text-subtitle-1 text-medium-emphasis mt-1">
                  Collected Surveys
                </div>
              </div>
              <VAvatar
                color="success"
                size="56"
                class="elevation-1"
              >
                <VIcon
                  icon="tabler-checkup-list"
                  size="32"
                />
              </VAvatar>
            </div>
          </VCard>
        </VCol>

        <!-- Not Collected Surveys Card -->
        <VCol
          cols="12"
          sm="4"
        >
          <VCard
            variant="flat"
            class="border pa-4"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h4 font-weight-bold warning--text">
                  {{ survey.number_of_not_collected_surveys }}
                </div>
                <div class="text-subtitle-1 text-medium-emphasis mt-1">
                  Not Collected Surveys
                </div>
              </div>
              <VAvatar
                color="warning"
                size="56"
                class="elevation-1"
              >
                <VIcon
                  icon="tabler-clipboard-off"
                  size="32"
                />
              </VAvatar>
            </div>
          </VCard>
        </VCol>

        <!-- Question Cards -->
        <VCol
          v-for="(question, questionIndex) in questions"
          :key="questionIndex"
          cols="12"
        >
          <VCard
            variant="flat"
            class="border"
          >
            <VCardItem>
              <VCardTitle
                class="text-h6"
                style="white-space: normal;"
              >
                {{ question.label }}
              </VCardTitle>
            </VCardItem>
            <VDivider />
            <VCardText>
              <HorizontalStackBar
                v-if="question.question_options.length <= 3"
                :graph-data="question.graph_data"
                @on-click-data-point="handleDataPointClick"
              />
              <VerticalBar
                v-else-if="question.question_options.length >= 9 || question.question_type === 'scale' || question.question_type === 'radio_rating' || question.question_type === 'star_rating' || question.question_type === 'smile'"
                :graph-data="question.graph_data"
                @on-click-data-point="handleDataPointClick"
              />
              <HorizontalBar
                v-else-if="question.question_options.length >= 4 && question.question_options.length <= 8"
                :graph-data="question.graph_data"
                @on-click-data-point="handleDataPointClick"
              />
            </VCardText>
          </VCard>
        </VCol>
      </template>

      <VCol v-else-if="!isLoading">
        <VCard
          variant="flat"
          class="border"
        >
          <VCardText>
            <span class="d-block text-center">
              No analytics data available for this survey
            </span>
          </VCardText>
        </VCard>
      </VCol>
    </template>
  </VRow>

  <!--
    <VCard class="mt-8">
    <VCardItem title="IF The Graph Data Count IS Above OR Equal to 5 ">
    <template #append>
    <MoreBtn />
    </template>
    </VCardItem>

    <VCardText>
    <HorizontalBar :graph-data="testingGraph" />
    </VCardText>
    </VCard>

    <VCard class="mt-8">
    <VCardItem title="IF The Question Type NPS">
    <template #append>
    <VBtn
    variant="tonal"
    append-icon="tabler-chevron-down"
    >
    January
    </VBtn>
    </template>
    </VCardItem>

    <VCardText>
    <VerticalBar :graph-data="testingGraph" />
    </VCardText>
    </VCard>
  -->

  <!-- Subscription Table Dialog -->
  <VDialog v-model="isSubscriptionDialogVisible">
    <DialogCloseBtn
      size="small"
      @click="() => (isSubscriptionDialogVisible = !isSubscriptionDialogVisible)"
    />

    <VCard>
      <VCardText class="pa-0">
        <SubscriptionTable
          :headers="[
            { title: 'Redirect', key: 'redirect', sortable: false },
            { title: 'UHID', key: 'customer.uhid' },
            { title: 'Name', key: 'customer.name' },
            { title: 'Phone', key: 'customer.phone', },
            { title: 'Discharge At', key: 'episode.discharge_date' },
            { title: 'Collected At', key: 'collected_at' },
          ]"
          :filters="subscriptionTableFilter"
        />
      </VCardText>
    </VCard>
  </VDialog>
</template>
